import {FC, useCallback, useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'

import FeatureHighlightSnackbar from './FeatureHighlightSnackbar'
import {
  hasFeatureHighlightPendingVisit,
  markFeatureHighlightPendingVisit,
  markFeatureHighlightSeen,
  shouldShowFeatureHighlight,
} from './storage'
import {FeatureHighlightMeta} from './types'

type FeatureHighlightProps = {
  feature: FeatureHighlightMeta
  /** Extra eligibility (role, tenant gates, feature flags). */
  enabled?: boolean
  /**
   * True while eligibility is still loading (not permanently ineligible).
   * Route visits in this state are stored as session pending visits.
   */
  isEligibilityPending?: boolean
}

/**
 * Generic one-time feature highlight host.
 * Pass a feature meta object; storage + snackbar copy come from that config.
 */
const FeatureHighlight: FC<FeatureHighlightProps> = ({
  feature,
  enabled = true,
  isEligibilityPending = false,
}) => {
  const history = useHistory()
  const {pathname} = useLocation()
  const [isOpen, setIsOpen] = useState(() =>
    shouldShowFeatureHighlight(feature.storageKey),
  )

  const isOnFeatureRoute =
    feature.routeMatch != null && pathname.includes(feature.routeMatch)
  const show = isOpen && enabled && !isOnFeatureRoute

  // Eligible visit → permanent seen. Pending eligibility → session pending visit.
  // Permanently ineligible visits write nothing (shared-browser safe).
  useEffect(() => {
    if (!isOnFeatureRoute) return

    if (enabled) {
      markFeatureHighlightSeen(feature.storageKey)
      setIsOpen(false)
      return
    }

    if (isEligibilityPending) {
      markFeatureHighlightPendingVisit(feature.storageKey)
      setIsOpen(false)
    }
  }, [enabled, feature.storageKey, isEligibilityPending, isOnFeatureRoute])

  // Promote a pending visit once eligibility resolves true (Vamshi race).
  useEffect(() => {
    if (!enabled) return
    if (!hasFeatureHighlightPendingVisit(feature.storageKey)) return
    markFeatureHighlightSeen(feature.storageKey)
    setIsOpen(false)
  }, [enabled, feature.storageKey])

  const dismissHighlight = useCallback(() => {
    // Product decision: once an admin dismisses this prompt, do not show it again.
    markFeatureHighlightSeen(feature.storageKey)
    setIsOpen(false)
  }, [feature.storageKey])

  const accept = useCallback(() => {
    markFeatureHighlightSeen(feature.storageKey)
    setIsOpen(false)
    if (feature.onAccept) {
      feature.onAccept({history})
      return
    }
    if (feature.targetPath) {
      history.push(feature.targetPath)
    }
  }, [feature, history])

  return (
    <FeatureHighlightSnackbar
      feature={feature}
      show={show}
      onClose={dismissHighlight}
      onOkay={accept}
    />
  )
}

export default FeatureHighlight
